import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const POPOVER_HEIGHT_ESTIMATE = 340;

const formatDateValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (value) => {
  if (!value) return '';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
};

const sameDay = (date, value) => formatDateValue(date) === value;

const buildCalendarDays = (visibleMonth) => {
  const year = visibleMonth.getFullYear();
  const month = visibleMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = firstDay.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const previousMonthDays = new Date(year, month, 0).getDate();
  const days = [];

  for (let index = startOffset - 1; index >= 0; index -= 1) {
    days.push(new Date(year, month - 1, previousMonthDays - index));
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    days.push(new Date(year, month, day));
  }

  while (days.length % 7 !== 0 || days.length < 42) {
    const nextDay = days.length - startOffset - daysInMonth + 1;
    days.push(new Date(year, month + 1, nextDay));
  }

  return days;
};

const DatePicker = ({ id, label, value, onChange, placeholder = 'Pick a date', className = '' }) => {
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const selectedDate = value ? new Date(`${value}T00:00:00`) : null;
  const [isOpen, setIsOpen] = useState(false);
  const [opensUp, setOpensUp] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState(null);
  const [visibleMonth, setVisibleMonth] = useState(selectedDate || new Date());
  const calendarDays = useMemo(() => buildCalendarDays(visibleMonth), [visibleMonth]);

  const updatePopoverPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 24);
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldOpenUp = spaceBelow < POPOVER_HEIGHT_ESTIMATE && spaceAbove > spaceBelow;

    setOpensUp(shouldOpenUp);
    setPopoverStyle({
      position: 'fixed',
      left: `${left}px`,
      width: `${width}px`,
      zIndex: 100001,
      ...(shouldOpenUp
        ? { bottom: `${window.innerHeight - rect.top + 8}px` }
        : { top: `${rect.bottom + 8}px` }),
    });
  };

  useEffect(() => {
    if (selectedDate) setVisibleMonth(selectedDate);
  }, [value]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClick = (event) => {
      if (
        !wrapperRef.current?.contains(event.target)
        && !popoverRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleReposition = () => updatePopoverPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen]);

  const moveMonth = (amount) => {
    setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + amount, 1));
  };

  const selectDate = (date) => {
    onChange(formatDateValue(date));
    setIsOpen(false);
  };

  const toggleCalendar = () => {
    if (!isOpen) {
      updatePopoverPosition();
    }

    setIsOpen((current) => !current);
  };

  const popover = (
    <div
      ref={popoverRef}
      className={`date-picker-popover ${opensUp ? 'opens-up' : ''}`}
      style={popoverStyle || undefined}
    >
      <div className="date-picker-header">
        <button type="button" onClick={() => moveMonth(-1)} aria-label="Previous month">
          <FaChevronLeft />
        </button>
        <strong>
          {new Intl.DateTimeFormat('en-ZA', { month: 'long', year: 'numeric' }).format(visibleMonth)}
        </strong>
        <button type="button" onClick={() => moveMonth(1)} aria-label="Next month">
          <FaChevronRight />
        </button>
      </div>

      <div className="date-picker-weekdays" aria-hidden="true">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
          <span key={`${day}-${index}`}>{day}</span>
        ))}
      </div>

      <div className="date-picker-grid">
        {calendarDays.map((date) => {
          const dateValue = formatDateValue(date);
          const isCurrentMonth = date.getMonth() === visibleMonth.getMonth();
          const isSelected = sameDay(date, value);
          const isToday = sameDay(date, formatDateValue(new Date()));

          return (
            <button
              key={dateValue}
              type="button"
              className={[
                'date-picker-day',
                isCurrentMonth ? '' : 'is-muted',
                isSelected ? 'is-selected' : '',
                isToday ? 'is-today' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => selectDate(date)}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div ref={wrapperRef} className={`date-picker ${className}`}>
      {label && <label htmlFor={id} className="date-picker-label">{label}</label>}
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className="date-picker-trigger"
        onClick={toggleCalendar}
        aria-expanded={isOpen}
      >
        <span className={value ? 'date-picker-value' : 'date-picker-placeholder'}>
          {value ? formatDisplayDate(value) : placeholder}
        </span>
        <FaCalendarAlt />
      </button>

      {isOpen && typeof document !== 'undefined' ? createPortal(popover, document.body) : null}
    </div>
  );
};

export default DatePicker;
